// src/hooks/useUserPreferences.js
// DB-backed settings — Supabase is the single source of truth.
//
// Load order:
//   1. useEffect fires after auth is ready (userId + authReady both set)
//   2. Fetches user_preferences + profiles.display_name in parallel
//   3. loading stays true until the fetch resolves or times out
//   4. On success: merges DB prefs on top of SETTINGS_DEFAULTS
//   5. On timeout (30s) or error: shows SETTINGS_DEFAULTS and sets error flag
//
// setSetting:
//   1. Optimistically updates local state immediately (UI feels synchronous)
//   2. Writes to DB in background
//   3. If DB write fails, reverts to last known good DB state

import { useState, useEffect, useCallback, useRef } from "react";
import { hasSupabase } from "../lib/supabaseClient";
import {
  getUserPreferences,
  upsertUserPreferences,
  getDisplayName,
} from "../lib/user/userService";
import { SETTINGS_DEFAULTS } from "./useSettings";

// ── merge helper ──────────────────────────────────────────────────────────────
function mergePrefs(dbPrefs, displayName) {
  return {
    ...SETTINGS_DEFAULTS,
    ...(dbPrefs ?? {}),
    displayName: displayName ?? dbPrefs?.displayName ?? SETTINGS_DEFAULTS.displayName,
    alerts: {
      ...SETTINGS_DEFAULTS.alerts,
      ...(dbPrefs?.alerts ?? {}),
    },
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// useUserPreferences
// userId — pass user?.id from useAuth. Pass null/undefined when logged out.
// ─────────────────────────────────────────────────────────────────────────────
export function useUserPreferences(userId, authReady = true) {
  const [settings,   setSettings]   = useState({ ...SETTINGS_DEFAULTS });
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);

  const lastGoodRef = useRef(null);

  // ── Load from DB ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!userId || !authReady) {
      setLoading(true);
      setError(false);
      return;
    }

    // No Supabase (local dev without .env) — show defaults immediately
    if (!hasSupabase) {
      setSettings({ ...SETTINGS_DEFAULTS });
      lastGoodRef.current = { ...SETTINGS_DEFAULTS };
      setLoading(false);
      return;
    }

    let mounted = true;
    setLoading(true);
    setError(false);

    // 30s timeout — long enough to survive a free-tier cold start (15-30s).
    const RETRY_DELAYS = [5000, 10000, 20000, 30000];
    const MAX_ATTEMPTS = 8;
    let retryTimer = null;

    function scheduleRetry(attempt) {
      if (!mounted) return;
      if (attempt >= MAX_ATTEMPTS) { if (mounted) setIsRetrying(false); return; }
      const delay = RETRY_DELAYS[Math.min(attempt, RETRY_DELAYS.length - 1)];
      retryTimer = setTimeout(async () => {
        if (!mounted) return;
        try {
          const [prefs, displayName] = await Promise.all([
            getUserPreferences(userId),
            getDisplayName(userId),
          ]);
          if (!mounted) return;
          const merged = mergePrefs(prefs, displayName);
          setSettings(merged);
          lastGoodRef.current = merged;
          setError(false);
          setIsRetrying(false);
        } catch {
          scheduleRetry(attempt + 1);
        }
      }, delay);
    }

    const timeout = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('prefs_timeout')), 30000)
    );

    Promise.race([
      Promise.all([getUserPreferences(userId), getDisplayName(userId)]),
      timeout,
    ])
      .then(([prefs, displayName]) => {
        if (!mounted) return;
        const merged = mergePrefs(prefs, displayName);
        setSettings(merged);
        lastGoodRef.current = merged;
        setLoading(false);
      })
      .catch(() => {
        if (!mounted) return;
        // Timed out or errored — show defaults, keep retrying in background
        setSettings({ ...SETTINGS_DEFAULTS });
        lastGoodRef.current = { ...SETTINGS_DEFAULTS };
        setError(true);
        setIsRetrying(true);
        setLoading(false);
        scheduleRetry(0);
      });

    return () => {
      mounted = false;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [userId, authReady]);

  // ── setSetting ─────────────────────────────────────────────────────────────
  const setSetting = useCallback((key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));

    if (!hasSupabase || !userId) return;

    upsertUserPreferences(userId, { [key]: value })
      .then(() => { setError(false); })
      .catch(async () => {
        try {
          const [fresh, freshName] = await Promise.all([
            getUserPreferences(userId),
            getDisplayName(userId),
          ]);
          const reverted = mergePrefs(fresh, freshName);
          setSettings(reverted);
          lastGoodRef.current = reverted;
        } catch {
          setError(true);
        }
      });
  }, [userId]);

  // ── resetDefaults ──────────────────────────────────────────────────────────
  const resetDefaults = useCallback(() => {
    const defaults = { ...SETTINGS_DEFAULTS };
    setSettings(defaults);
    lastGoodRef.current = defaults;
    if (hasSupabase && userId) {
      upsertUserPreferences(userId, defaults).catch(() => setError(true));
    }
  }, [userId]);

  return { settings, setSetting, resetDefaults, loading, error, isRetrying };
}
